import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/dmarc";

// Conversión robusta para valores que representan booleanos
function toBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  if (
    [
      "true",
      "t",
      "1",
      "yes",
      "y",
      "ok",
      "si",
      "sí",
      "pass",
      "aligned",
    ].includes(s)
  )
    return true;
  if (
    ["false", "f", "0", "no", "n", "fail", "failed", "not aligned"].includes(s)
  )
    return false;
  // Si llega algo raro (p.ej. números), intenta parsear
  const n = Number(v);
  if (Number.isFinite(n)) return n !== 0;
  return false;
}

const BoolPill = ({ value }) => {
  const on = toBool(value);
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs ${on ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
    >
      {on ? "Sí" : "No"}
    </span>
  );
};

export default function IpDetallePage() {
  const { ip: ipFromPath } = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);

  // Soporta /ip/:ip y /ip-detalle?ip=
  const ip = ipFromPath || qs.get("ip") || "";
  const dominio = qs.get("dominio") || "";
  const desde = qs.get("desde") || "";
  const hasta = qs.get("hasta") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API_BASE}/detalle_ip_no_dmarc`, {
          params: { ip, dominio, desde, hasta },
          timeout: 15000,
        });
        // Asegura arreglo
        const arr = Array.isArray(data) ? data : data ? [data] : [];
        setRows(arr);
      } catch (e) {
        console.error(e);
        setError(
          e?.response?.data?.error || e.message || "Error al consultar detalle",
        );
      } finally {
        setLoading(false);
      }
    };
    if (ip) run();
    else {
      setRows([]);
      setLoading(false);
      setError("Falta el parámetro de IP.");
    }
  }, [ip, dominio, desde, hasta]);

  const header = rows?.[0] || {};
  const org = header?.reportorgname || "-";
  const dom = header?.domain || dominio || "-";

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Detalle IP</h1>
          <a href="/" className="text-sm text-blue-600 hover:underline">
            ← Volver al panel
          </a>
        </div>

        <div className="text-gray-700">
          <div>
            <span className="font-medium">IP:</span>{" "}
            <span className="font-mono">{ip || "-"}</span>
          </div>
          <div>
            <span className="font-medium">Dominio:</span> {dom}
          </div>
          <div>
            <span className="font-medium">Origen (report org):</span> {org}
          </div>
          <div>
            <span className="font-medium">Rango:</span> {desde} → {hasta}
          </div>
        </div>

        {loading && (
          <div className="p-3 bg-white rounded-xl shadow">Cargando...</div>
        )}
        {error && !loading && (
          <div className="p-3 bg-red-100 text-red-800 rounded-xl shadow">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-2xl shadow p-4 overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-3 py-2">RecordID</th>
                  <th className="px-3 py-2">SourceIP</th>
                  <th className="px-3 py-2">Domain</th>
                  <th className="px-3 py-2">Report Org</th>
                  <th className="px-3 py-2">Count</th>
                  <th className="px-3 py-2">DKIM Aligned</th>
                  <th className="px-3 py-2">SPF Aligned</th>
                  <th className="px-3 py-2">Disposition</th>
                  <th className="px-3 py-2">AuthType</th>
                  <th className="px-3 py-2">Result</th>
                  <th className="px-3 py-2">Selector</th>
                  <th className="px-3 py-2">SPF Domain</th>
                  <th className="px-3 py-2">DKIM Domain</th>
                </tr>
              </thead>
              <tbody>
                {rows?.length ? (
                  rows.map((r, i) => (
                    <tr
                      key={`${r.recordid ?? i}-${r.authtype ?? "-"}-${i}`}
                      className="border-b last:border-0"
                    >
                      <td className="px-3 py-2 font-mono">
                        {r.recordid ?? "-"}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {r.sourceip ?? "-"}
                      </td>
                      <td className="px-3 py-2">{r.domain ?? "-"}</td>
                      <td className="px-3 py-2">{r.reportorgname ?? "-"}</td>
                      <td className="px-3 py-2">{r.count ?? 0}</td>
                      <td className="px-3 py-2">
                        <BoolPill value={r.dkimaligned} />
                      </td>
                      <td className="px-3 py-2">
                        <BoolPill value={r.spfaligned} />
                      </td>
                      <td className="px-3 py-2">{r.disposition ?? "-"}</td>
                      <td className="px-3 py-2">{r.authtype ?? "-"}</td>
                      <td className="px-3 py-2">{r.result ?? "-"}</td>
                      <td className="px-3 py-2">{r.selector ?? "-"}</td>
                      <td className="px-3 py-2">{r.spfdomain ?? "-"}</td>
                      <td className="px-3 py-2">{r.dkimdomain ?? "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-3 py-4 text-gray-500" colSpan={13}>
                      Sin filas para esta IP en el rango.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
